import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Loader, Eye, EyeOff } from 'lucide-react';
import { SeasonalBanner } from '../../../types';
import ImageUploader from '../common/ImageUploader';

const DEFAULT_BANNER: SeasonalBanner = {
  visible: true,
  backgroundImage: '',
  overlayOpacity: 40,
  eyebrow: 'NEW SEASON',
  title: 'SUMMER 2026',
  subtitle: '',
  ctaText: 'PREVIEW DROPS',
  ctaLink: '/shop/soon',
};

const SeasonalBannerManager: React.FC = () => {
  const [banner, setBanner] = useState<SeasonalBanner>(DEFAULT_BANNER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const docRef = doc(db, 'content', 'home');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().seasonalBanner) {
        setBanner({ ...DEFAULT_BANNER, ...docSnap.data().seasonalBanner });
      }
    } catch (error) {
      console.error('Error fetching seasonal banner:', error);
      setMessage({ type: 'error', text: 'Failed to load seasonal banner settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'content', 'home'),
        {
          seasonalBanner: banner,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setMessage({ type: 'success', text: 'Seasonal banner saved successfully' });
    } catch (error) {
      console.error('Error saving seasonal banner:', error);
      setMessage({ type: 'error', text: 'Failed to save seasonal banner' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof SeasonalBanner, value: string | number | boolean) => {
    setBanner((prev) => ({ ...prev, [field]: value }));
  };

  if (loading)
    return (
      <div className="py-8">
        <Loader className="animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-xl">Seasonal Banner</h2>
        <div className="flex items-center gap-4">
          {/* Visibility Toggle */}
          <button
            onClick={() => updateField('visible', !banner.visible)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-heading font-bold uppercase tracking-widest border transition-colors ${
              banner.visible
                ? 'bg-green-50 border-green-300 text-green-800 hover:bg-green-100'
                : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
            }`}
          >
            {banner.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            {banner.visible ? 'Visible' : 'Hidden'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`p-3 mb-6 text-sm ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-8">
        Configure the full-width seasonal campaign banner that appears between the brand product highlights and the Latest feed on the homepage. Use the visibility toggle to show or hide the section.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image & live preview */}
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Background Image & Preview</h3>
          <ImageUploader
            value={banner.backgroundImage}
            onChange={(url) => updateField('backgroundImage', url)}
            pathPrefix="content/seasonal-banner"
            aspectClass="aspect-[16/10]"
            onError={(text) => setMessage({ type: 'error', text })}
            onUploaded={() => setMessage({ type: 'success', text: 'Image uploaded successfully' })}
            previewOverlay={
              <>
                {banner.backgroundImage && (
                  <div className="absolute inset-0 bg-black" style={{ opacity: banner.overlayOpacity / 100 }} />
                )}
                {banner.backgroundImage && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 space-y-2">
                    {banner.eyebrow && (
                      <span className="font-heading text-[9px] tracking-[0.3em] uppercase text-white/70">
                        {banner.eyebrow}
                      </span>
                    )}
                    <p className="font-heading text-2xl font-bold tracking-widest text-white leading-none">
                      {banner.title || 'TITLE'}
                    </p>
                    {banner.subtitle && (
                      <p className="font-heading text-[10px] tracking-[0.25em] text-white uppercase">
                        {banner.subtitle}
                      </p>
                    )}
                    {banner.ctaText && (
                      <span className="inline-block border border-white px-4 py-1.5 mt-2 text-[9px] text-white tracking-widest uppercase">
                        {banner.ctaText}
                      </span>
                    )}
                  </div>
                )}
                {!banner.visible && (
                  <div className="absolute top-3 left-3 bg-red-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1">
                    Hidden
                  </div>
                )}
              </>
            }
          />
        </div>

        {/* Settings */}
        <div className="space-y-4">
          {/* Eyebrow */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
              Eyebrow
            </label>
            <input
              type="text"
              value={banner.eyebrow}
              onChange={(e) => updateField('eyebrow', e.target.value)}
              className="w-full text-sm p-2 border border-gray-200 font-heading uppercase tracking-widest"
              placeholder="NEW SEASON"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
              Title
            </label>
            <input
              type="text"
              value={banner.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="w-full text-sm p-2 border border-gray-200 font-heading uppercase tracking-widest"
              placeholder="SPRING 2026"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
              Subtitle
            </label>
            <input
              type="text"
              value={banner.subtitle}
              onChange={(e) => updateField('subtitle', e.target.value)}
              className="w-full text-sm p-2 border border-gray-200"
              placeholder="COLLECTION LAUNCHING SOON"
            />
          </div>

          {/* CTA Button */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Button Text
              </label>
              <input
                type="text"
                value={banner.ctaText}
                onChange={(e) => updateField('ctaText', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                placeholder="PREVIEW DROPS"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Button Link
              </label>
              <input
                type="text"
                value={banner.ctaLink}
                onChange={(e) => updateField('ctaLink', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200 font-mono"
                placeholder="/shop/soon"
              />
            </div>
          </div>

          {/* Overlay Opacity */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
              Overlay Opacity: {banner.overlayOpacity}%
            </label>
            <input
              type="range"
              min="0"
              max="80"
              value={banner.overlayOpacity}
              onChange={(e) => updateField('overlayOpacity', parseInt(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-gray-400 mt-1">
              Higher values darken the image for better text readability
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalBannerManager;
